import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from '../service/layout.service';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { SidebarModule } from 'primeng/sidebar';
import { AuthService } from '@app/core/services/auth.service';
import { WalkthroughService } from '@app/core/services/walkthrough.service';
import { ApiService } from '@app/core/services/api.service';
import {
    DEFAULT_SHOP_ID,
    persistShopId,
    resolveShopIdForRequest,
} from '@app/core/storage/shop-id.storage';
import { ShopsStore } from '@app/core/stores/shops.store';
import { combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface AppEntry {
    label: string;
    description: string;
    icon: string;
    color: string;
    route: string;
}

interface AppCategory {
    title: string;
    apps: AppEntry[];
}

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, MenuModule, SelectModule, SidebarModule, TooltipModule],
    template: `
    <div class="layout-topbar">
        <div class="layout-topbar-logo-container">
            @if (moduleLabel) {
                <div class="layout-topbar-module">
                    <a class="layout-topbar-logo" routerLink="/">
                        <img src="assets/images/logo/logo-monicase-light.png" class="logo light" alt="MONI" style="height:2.2rem;padding:0" />
                        <img src="assets/images/logo/logo-monicase.png" class="logo dark" alt="MONI" style="height:2.2rem;padding:0" />
                    </a>
                    <span class="layout-topbar-module-sep">/</span>
                    <span class="layout-topbar-module-badge" [style.background]="moduleColor || 'var(--primary-color)'">
                        {{ moduleLabel }}
                    </span>
                </div>
            } @else {
                <a class="layout-topbar-logo" routerLink="/">
                    <img src="assets/images/logo/logo-monicase-light.png" class="logo light" alt="MONI" />
                    <img src="assets/images/logo/logo-monicase.png" class="logo dark" alt="MONI" />
                </a>
            }
            @if (!hideMenuButton) {
                <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()">
                    <i class="pi pi-bars"></i>
                </button>
            }
        </div>

        <div class="layout-topbar-actions">
            @if (showShopSwitcher) {
                <div class="layout-topbar-shop-select mr-2">
                    <p-select
                        [options]="shopSelectOptions"
                        optionLabel="label"
                        optionValue="id"
                        [(ngModel)]="selectedShopId"
                        (ngModelChange)="onShopChange($event)"
                        [style]="{ minWidth: '12rem' }"
                        appendTo="body"
                        inputId="topbar-shop"
                    />
                </div>
            }

            <button class="layout-topbar-menu-button layout-topbar-action" pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                <i class="pi pi-ellipsis-v"></i>
            </button>

            <div class="layout-topbar-menu hidden lg:block">
                <div class="layout-topbar-menu-content">
                    <button type="button" class="layout-topbar-action" data-tour="walkthrough-btn"
                            pTooltip="Guide d'utilisation" tooltipPosition="bottom"
                            (click)="walkthroughService.launchForCurrentRoute()">
                        <i class="pi pi-question-circle"></i>
                    </button>
                    <button type="button" class="layout-topbar-action" data-tour="app-drawer-btn" (click)="showAppDrawer.set(true)">
                        <i class="pi pi-th-large"></i>
                    </button>
                    <button type="button" class="layout-topbar-action" data-tour="profile-btn" (click)="menu.toggle($event)">
                        <i class="pi pi-user"></i>
                        <span>Profile</span>
                    </button>
                    <p-menu #menu [model]="profileItemsMenu" [popup]="true" />
                </div>
            </div>
        </div>
    </div>

    <!-- App drawer -->
    <p-sidebar [(visible)]="showAppDrawerValue" position="right" [style]="{ width: '380px' }" [showCloseIcon]="false" appendTo="body">
        <ng-template pTemplate="header">
            <div class="app-drawer-header">
                <span class="app-drawer-title">Toutes les applications</span>
                <button class="app-drawer-close" (click)="showAppDrawer.set(false)">
                    <i class="pi pi-times"></i>
                </button>
            </div>
        </ng-template>
        <ng-template pTemplate="content">
            <div class="app-drawer-body">
                @for (cat of appCategories; track cat.title) {
                    <div class="app-category">
                        <span class="app-category-title">{{ cat.title }}</span>
                        <div class="app-list">
                            @for (app of cat.apps; track app.label) {
                                <div class="app-card" (click)="navigateTo(app.route)">
                                    <div class="app-card-icon" [style.background]="app.color + '18'" [style.color]="app.color">
                                        <i [class]="app.icon"></i>
                                    </div>
                                    <div class="app-card-info">
                                        <span class="app-card-name">{{ app.label }}</span>
                                        <span class="app-card-desc">{{ app.description }}</span>
                                    </div>
                                    <i class="pi pi-chevron-right app-card-arrow"></i>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        </ng-template>
    </p-sidebar>
    `,
    styles: [`
        .layout-topbar-module {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .layout-topbar-module-sep {
            font-size: 1rem;
            opacity: 0.35;
            color: var(--text-color);
            font-weight: 300;
        }
        .layout-topbar-module-badge {
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #fff;
            padding: 0.25rem 0.65rem;
            border-radius: 6px;
        }

        /* ── Drawer ── */
        .app-drawer-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: 0.25rem 0;
        }
        .app-drawer-title {
            font-size: 1.05rem;
            font-weight: 600;
            color: var(--text-color);
        }
        .app-drawer-close {
            background: none;
            border: none;
            cursor: pointer;
            color: var(--text-color);
            width: 2rem;
            height: 2rem;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.15s;
        }
        .app-drawer-close:hover {
            background: var(--surface-hover);
            color: var(--red-500);
        }
        .app-drawer-body {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            padding: 0.25rem 0;
        }
        .app-category {
            display: flex;
            flex-direction: column;
            gap: 0.6rem;
        }
        .app-category-title {
            font-size: 0.7rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--text-color);
            padding: 0 0.25rem;
        }
        .app-list {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
        }
        .app-card {
            display: flex;
            align-items: center;
            gap: 0.9rem;
            padding: 0.75rem 0.85rem;
            border-radius: 10px;
            cursor: pointer;
            transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
            background: var(--surface-overlay);
        }
        .app-card:hover {
            background: var(--surface-hover);
            border-color: var(--primary-300, #a5b4fc);
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .app-card:hover .app-card-arrow {
            opacity: 1;
        }
        .app-card-icon {
            width: 44px;
            height: 44px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.35rem;
            flex-shrink: 0;
        }
        .app-card-info {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 0.15rem;
        }
        .app-card-name {
            font-size: 0.92rem;
            font-weight: 600;
            color: var(--text-color);
        }
        .app-card-desc {
            font-size: 0.78rem;
            color: var(--text-color);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .app-card-arrow {
            font-size: 0.75rem;
            color: var(--text-color);
            opacity: 0;
            transition: opacity 0.15s;
            flex-shrink: 0;
        }
    `]
})
export class AppTopbar implements OnInit {
    /** Masque le bouton d'ouverture du menu (ex. page wizard de bienvenue) */
    @Input() hideMenuButton = false;
    /** Nom du module courant affiché à côté du logo (ex. 'SKEDI') */
    @Input() moduleLabel: string | null = null;
    /** Couleur de fond du badge module */
    @Input() moduleColor: string | null = null;

    private platformId = inject(PLATFORM_ID);
    private isBrowser = isPlatformBrowser(this.platformId);

    showShopSwitcher = false;
    shopSelectOptions: { id: number; label: string }[] = [];
    selectedShopId = DEFAULT_SHOP_ID;
    showAppDrawer = signal(false);

    // p-sidebar two-way binding helper
    get showAppDrawerValue() { return this.showAppDrawer(); }
    set showAppDrawerValue(v: boolean) { this.showAppDrawer.set(v); }

    profileItemsMenu: MenuItem[] = [
        {
            label: 'Mon profil',
            icon: 'pi pi-user',
            command: () => { this._router.navigate(['moni/profile']); }
        },
        {
            label: 'Déconnexion',
            icon: 'pi pi-sign-out',
            command: () => { this._authService.logout(); }
        },
    ];

    readonly appCategories: AppCategory[] = [
        {
            title: 'Gestion commerciale',
            apps: [
                {
                    label: 'MONI',
                    description: 'Devis, factures et gestion des ventes',
                    icon: 'fa-regular fa-file-invoice',
                    color: '#3B82F6',
                    route: 'moni',
                },
                {
                    label: 'LOGISTIKI',
                    description: 'Comptabilité et gestion des dépenses',
                    icon: 'fa-duotone fa-solid fa-building-columns',
                    color: '#8B5CF6',
                    route: 'moni/depenses',
                },
            ],
        },
        {
            title: 'Point de vente',
            apps: [
                {
                    label: 'POLISI',
                    description: 'Caisse enregistreuse et paiements',
                    icon: 'fa-duotone fa-solid fa-cash-register',
                    color: '#10B981',
                    route: 'pos',
                },
            ],
        },
        {
            title: 'Productivité',
            apps: [
                {
                    label: 'SKEDI',
                    description: 'Gestion de projets et de tâches',
                    icon: 'fa-duotone fa-solid fa-diagram-subtask',
                    color: '#6366F1',
                    route: 'skedi',
                },
            ],
        },
        {
            title: 'Réservation & location',
            apps: [
                {
                    label: 'KRATISI',
                    description: 'Hébergements, véhicules et réservations',
                    icon: 'fa-duotone fa-solid fa-calendar-check',
                    color: '#0EA5E9',
                    route: 'kratisi',
                },
            ],
        },
        {
            title: 'Autres applications',
            apps: [
                {
                    label: 'EEZILI',
                    description: 'Présence digitale et site web',
                    icon: 'fa-duotone fa-solid fa-computer',
                    color: '#F59E0B',
                    route: 'cms',
                },
                {
                    label: 'KLEIDI',
                    description: 'Gestion des accès et sécurité',
                    icon: 'fa-duotone fa-solid fa-lock-keyhole',
                    color: '#EF4444',
                    route: 'kleidi',
                },
            ],
        },
    ];

    constructor(
        public layoutService: LayoutService,
        public walkthroughService: WalkthroughService,
        private _authService: AuthService,
        private _router: Router,
        private shopsStore: ShopsStore,
        private api: ApiService
    ) {}

    ngOnInit(): void {
        this.selectedShopId = resolveShopIdForRequest(this.isBrowser);

        const isSuperAdmin = this._authService.hasRole('super-admin');

        // For super-admins: null (all shops). For others: fetch restricted shop IDs.
        const accessibleShopIds$ = (isSuperAdmin || !this._authService.isAuthenticated())
            ? of(null as number[] | null)
            : this.api.get<any>('skedi/spaces/my-accessible-shop-ids').pipe(
                map((res: any) => res.data as number[] | null),
                catchError(() => of(null as number[] | null))
            );

        combineLatest([this.shopsStore.shops$, accessibleShopIds$]).subscribe(([shops, accessibleIds]) => {
            const filteredShops = (accessibleIds === null)
                ? shops
                : shops.filter((s: any) => (accessibleIds as number[]).includes(Number(s.id)));

            this.showShopSwitcher = (accessibleIds === null)
                ? filteredShops.length > 1
                : filteredShops.length > 1;

            if (!this.showShopSwitcher) return;

            this.shopSelectOptions = [
                { id: -1, label: 'Tous les magasins' },
                ...filteredShops.map((s: any) => ({
                    id: Number(s.id),
                    label: (s.name as string) ?? (s.name?.fr as string) ?? `Magasin ${s.id}`,
                })),
            ];

            const allowed = new Set(this.shopSelectOptions.map((o) => o.id));
            if (!allowed.has(this.selectedShopId)) {
                const firstId = this.shopSelectOptions.find((o) => o.id !== -1)?.id ?? DEFAULT_SHOP_ID;
                this.selectedShopId = firstId;
                persistShopId(this.isBrowser, this.selectedShopId);
            }
        });
    }

    navigateTo(route: string) {
        this._router.navigate([route]);
        this.showAppDrawer.set(false);
    }

    onShopChange(shopId: number): void {
        if (shopId === undefined || shopId === null || !Number.isFinite(Number(shopId))) return;
        persistShopId(this.isBrowser, Number(shopId));
        if (this.isBrowser) {
            sessionStorage.setItem('moni.pendingShopChange', String(shopId));
            globalThis.location.reload();
        }
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }
}
